import type { VacationKind, VacationStatus } from "./types";

export const VACATION_LABELS: Record<VacationKind, string> = {
  semester: "Semester",
  kompledigt: "Kompledigt",
  tjanstledigt: "Tjänstledigt",
  foraldraledigt: "Föräldraledigt",
};

export const VACATION_STATUS_LABELS: Record<VacationStatus, string> = {
  vantande: "Väntar på svar",
  godkand: "Godkänd",
  nekad: "Nekad",
  aterkallad: "Återkallad",
};

/**
 * Frånvarokoder i Fortnox för respektive ledighetstyp. Ledighet rapporteras som
 * frånvarotransaktioner (`/absencetransactions`), inte närvaro — koderna nedan
 * är ogiltiga på närvaroendpointen.
 *
 * Obs: kompledigt är `KOM`, inte `FLX`. `FLX` är flextidssaldo och hör till
 * närvarosidan.
 */
export const VACATION_CAUSE_CODES: Record<VacationKind, string> = {
  semester: "SEM",
  kompledigt: "KOM",
  tjanstledigt: "TJL",
  foraldraledigt: "FPE",
};

/**
 * Kvarvarande semesterdagar enligt Fortnox.
 *
 * `vacation_paid_days` är bruttot för semesteråret, så uttagna och registrerade
 * dagar måste dras av. Sparade dagar från tidigare år läggs till — de går också
 * att ta ut. Ger null om Fortnox inte lämnat några siffror alls, så att
 * gränssnittet kan visa att saldot är okänt i stället för att påstå noll.
 */
export function vacationDaysLeft(balance: {
  vacation_paid_days: number | null;
  vacation_saved_days: number | null;
  vacation_used_days: number | null;
  vacation_pending_days: number | null;
}): number | null {
  const { vacation_paid_days, vacation_saved_days, vacation_used_days, vacation_pending_days } =
    balance;

  if (
    vacation_paid_days === null &&
    vacation_saved_days === null &&
    vacation_used_days === null &&
    vacation_pending_days === null
  ) {
    return null;
  }

  const left =
    (vacation_paid_days ?? 0) +
    (vacation_saved_days ?? 0) -
    (vacation_used_days ?? 0) -
    (vacation_pending_days ?? 0);

  return Math.round(left * 10) / 10;
}

/**
 * Frånvaro som är semestergrundande. Semester tjänar inte in ny semester, men
 * föräldraledighet gör det upp till en gräns — Fortnox räknar ut det själv, vi
 * anger bara flaggan.
 */
export const HOLIDAY_ENTITLING: Record<VacationKind, boolean> = {
  semester: false,
  kompledigt: false,
  tjanstledigt: false,
  foraldraledigt: true,
};

/* -------------------------------------------------------------------------- */
/* Röda dagar                                                                  */
/* -------------------------------------------------------------------------- */

/** Påskdagen enligt den anonyma gregorianska algoritmen. */
function easterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function iso(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;
}

function addDays(date: Date, days: number): Date {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
}

const holidayCache = new Map<number, Set<string>>();

/**
 * Midsommarafton, alltså fredagen före midsommardagen. Midsommardagen är den
 * lördag som faller 20–26 juni, så aftonen är fredagen 19–25 juni.
 */
function midsummerEve(year: number): string {
  for (let day = 19; day <= 25; day++) {
    const date = new Date(year, 5, day);
    if (date.getDay() === 5) return iso(date);
  }
  throw new Error(`Hittade ingen midsommarafton för ${year}.`);
}

/**
 * Svenska helgdagar som kan falla på en vardag. Midsommardagen och Alla helgons
 * dag ligger alltid på en lördag och behöver därför inte listas.
 *
 * Listan är avstämd mot Fortnox heltidsschema (`HEL`) och innehåller därför även
 * julafton, nyårsafton och midsommarafton. De är inte allmänna helgdagar, men
 * räknas som lediga i praktiken och är nolltimmarsdagar i Fortnox.
 */
export function swedishHolidays(year: number): Set<string> {
  const cached = holidayCache.get(year);
  if (cached) return cached;

  const easter = easterSunday(year);
  const days = new Set<string>([
    `${year}-01-01`, // Nyårsdagen
    `${year}-01-06`, // Trettondedag jul
    `${year}-05-01`, // Första maj
    `${year}-06-06`, // Sveriges nationaldag
    `${year}-12-24`, // Julafton
    `${year}-12-25`, // Juldagen
    `${year}-12-26`, // Annandag jul
    `${year}-12-31`, // Nyårsafton
    iso(addDays(easter, -2)), // Långfredagen
    iso(addDays(easter, 1)), // Annandag påsk
    iso(addDays(easter, 39)), // Kristi himmelsfärdsdag
    midsummerEve(year),
  ]);

  holidayCache.set(year, days);
  return days;
}

export function isWorkday(date: Date): boolean {
  const weekday = date.getDay();
  if (weekday === 0 || weekday === 6) return false;
  return !swedishHolidays(date.getFullYear()).has(iso(date));
}

/* -------------------------------------------------------------------------- */
/* Beräkningar                                                                 */
/* -------------------------------------------------------------------------- */

function parseDate(value: string): Date {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Antal arbetsdagar mellan två datum (inklusive båda), exklusive helger och
 * svenska röda dagar. Detta är det som räknas av från semestersaldot.
 */
export function countWorkdays(startDate: string, endDate: string): number {
  const start = parseDate(startDate);
  const end = parseDate(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return 0;

  let days = 0;
  for (let day = start; day <= end; day = addDays(day, 1)) {
    if (isWorkday(day)) days++;
  }
  return days;
}

/** Alla datum i intervallet som är arbetsdagar, som `YYYY-MM-DD`. */
export function workdaysBetween(startDate: string, endDate: string): string[] {
  const start = parseDate(startDate);
  const end = parseDate(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return [];

  const dates: string[] = [];
  for (let day = start; day <= end; day = addDays(day, 1)) {
    if (isWorkday(day)) dates.push(iso(day));
  }
  return dates;
}

/** Läsvänligt intervall, t.ex. "3 aug – 14 aug 2026". */
export function formatDateRange(startDate: string, endDate: string): string {
  const format = new Intl.DateTimeFormat("sv-SE", { day: "numeric", month: "short" });
  const start = parseDate(startDate);
  const end = parseDate(endDate);
  if (startDate === endDate) {
    return `${format.format(start)} ${start.getFullYear()}`;
  }
  return `${format.format(start)} – ${format.format(end)} ${end.getFullYear()}`;
}
